import React, { useState } from 'react';
import { Button, message, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { extractYear } from './extractYear';

interface DataCollBasisDownloadProps {
  dataCollName: string;
  client: { id: string; token: string };
  hasBasisTable: boolean;
}

export const DataCollBasisDownload: React.FC<DataCollBasisDownloadProps> = ({
  dataCollName,
  client,
  hasBasisTable,
}) => {
  const [loading, setLoading] = useState(false);

  const handleDownload = () => {
    if (!client?.id || !dataCollName) return;

    const year = extractYear(dataCollName);
    if (!year) {
      message.error('Jahr konnte nicht aus dem Namen der Datensammlung extrahiert werden');
      return;
    }

    setLoading(true);
    axios
      .post(
        `${process.env.REACT_APP_API_URL}/b2zero/${client.id}/download_data_coll_basis`,
        { year },
        {
          headers: { Authorization: `Bearer ${client.token}` },
          responseType: 'blob',
        }
      )
      .then((response) => {
        // Extract filename from Content-Disposition header (backend sets correct name)
        const contentDisposition = response.headers['content-disposition'];
        let filename = `c${client.id.padStart(3, '0')}_Stammdatentabelle_${year}.xlsx`;
        if (contentDisposition) {
          // Match filename="value" or filename=value (stop at semicolon or end)
          const filenameMatch = contentDisposition.match(/filename=["']?([^"';]+)["']?/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].trim();
          }
        }

        // Create a download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        message.success(`Stammdatentabelle erfolgreich heruntergeladen`, 6);
      })
      .catch((error: any) => {
        const prefix = 'Fehler beim Herunterladen der Stammdatentabelle: ';
        const showError = (msg: string) => {
          // Ensure msg is a string
          const msgStr = String(msg || 'Unbekannter Fehler');
          if (msgStr.trim().toLowerCase().startsWith(prefix.trim().toLowerCase())) {
            message.error(msgStr, 6);
          } else {
            message.error(`${prefix}${msgStr}`, 6);
          }
          Sentry.captureException(error);
        };

        // If error.response.data is a Blob, try to parse it as JSON
        if (
          error.response &&
          error.response.data instanceof Blob &&
          error.response.data.type === 'application/json'
        ) {
          error.response.data.text().then((text: string) => {
            let errorMessage = 'Unbekannter Fehler';
            try {
              const json = JSON.parse(text);
              errorMessage = json.error || json.message || text;
            } catch {
              errorMessage = text;
            }
            showError(errorMessage);
          });
        } else {
          let errorMessage =
            error.response?.data?.error || error.response?.data?.message || 'Unbekannter Fehler';
          if (typeof errorMessage === 'object' && errorMessage !== null) {
            errorMessage = errorMessage.message || JSON.stringify(errorMessage);
          }
          showError(errorMessage);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Tooltip title="Stammdatentabelle herunterladen">
      <Button
        icon={<DownloadOutlined />}
        onClick={handleDownload}
        loading={loading}
        disabled={!client?.id || !dataCollName}
        type="primary"
      >
        Stammdatentabelle herunterladen
      </Button>
    </Tooltip>
  );
};
